import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiTrash2, FiMinus, FiPlus, FiShoppingBag } from 'react-icons/fi';
import { useCart } from '@/contexts/CartContext';
import { formatCurrency } from '@/utils/formatters';

const Cart = () => {
  const navigate = useNavigate();
  const { cart, updateQuantity, removeFromCart, getCartTotal } = useCart();

  const storeIds = Object.keys(cart);
  const isEmpty = storeIds.length === 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <FiArrowLeft className="text-xl" />
          </button>
          <h1 className="font-semibold text-gray-800">Carrinho</h1>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {isEmpty ? (
          <div className="text-center py-12">
            <FiShoppingBag className="mx-auto text-5xl text-gray-300 mb-4" />
            <p className="text-gray-500 mb-4">Seu carrinho está vazio</p>
            <button
              onClick={() => navigate('/cliente')}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Continuar Comprando
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {storeIds.map((storeId) => {
              const storeCart = cart[storeId];
              const storeTotal = getCartTotal(storeId);

              return (
                <div key={storeId} className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b">
                    <h3 className="font-semibold text-gray-800">{storeCart.storeName}</h3>
                  </div>

                  <div className="divide-y">
                    {storeCart.items.map((item) => {
                      const defaultImage = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjgwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik00MCAyOEMzNS41ODE3IDI4IDMyIDMxLjU4MTcgMzIgMzZDMzIgNDAuNDE4MyAzNS41ODE3IDQ0IDQwIDQ0QzQ0LjQxODMgNDQgNDggNDAuNDE4MyA0OCAzNkM0OCAzMS41ODE3IDQ0LjQxODMgMjggNDAgMjhaIiBmaWxsPSIjOUI5QkEzIi8+CjxwYXRoIGQ9Ik0yNiA1MkMyNiA0Ny41ODE3IDI5LjU4MTcgNDQgMzQgNDRINDZDNTAuNDE4MyA0NCA1NCA0Ny41ODE3IDU0IDUyVjUySDI2VjUyWiIgZmlsbD0iIzlCOUJBMyIvPgo8L3N2Zz4=';
                      
                      // Support both Cloudinary URLs, object format {url, public_id}, and legacy Base64 images
                      const getProductImage = () => {
                        if (!item.product.photos || item.product.photos.length === 0) return defaultImage;
                        const photo = item.product.photos[0];
                        if (typeof photo === 'object' && photo?.url) return photo.url;
                        if (typeof photo === 'string') {
                          if (photo.startsWith('http://') || photo.startsWith('https://')) return photo;
                          if (photo.startsWith('data:')) return photo;
                          return `data:image/jpeg;base64,${photo}`;
                        }
                        return defaultImage;
                      };
                      const image = getProductImage();

                      return (
                        <div key={item.product.id} className="p-4 flex gap-4">
                          <img
                            src={image}
                            alt={item.product.name}
                            className="w-20 h-20 object-cover rounded-lg"
                            onError={(e) => { e.target.src = defaultImage; }}
                          />
                          
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-800">{item.product.name}</h4>
                            <p className="text-[#DC143C] font-semibold">
                              {formatCurrency(item.product.price)}
                            </p>
                            
                            <div className="flex items-center justify-between mt-2">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => updateQuantity(storeId, item.product.id, item.quantity - 1)}
                                  className="w-8 h-8 rounded-full border flex items-center justify-center hover:bg-gray-50"
                                >
                                  <FiMinus className="text-sm" />
                                </button>
                                <span className="w-8 text-center">{item.quantity}</span>
                                <button
                                  onClick={() => updateQuantity(storeId, item.product.id, item.quantity + 1)}
                                  className="w-8 h-8 rounded-full border flex items-center justify-center hover:bg-gray-50"
                                >
                                  <FiPlus className="text-sm" />
                                </button>
                              </div>
                              
                              <button
                                onClick={() => removeFromCart(storeId, item.product.id)}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-full"
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="bg-gray-50 px-4 py-3 flex items-center justify-between">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-semibold text-lg">{formatCurrency(storeTotal)}</span>
                  </div>

                  <div className="p-4">
                    <button
                      onClick={() => navigate(`/cliente/checkout/${storeId}`)}
                      className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors"
                    >
                      Finalizar Compra
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default Cart;
